import { ChangeEvent } from 'react';
import { Controller } from 'react-hook-form';
import {
  ClickOutsideDetector,
  IconClose,
  IconDown,
  IconSelected,
  IconUp,
  InputSize,
  InputText,
} from '@ui/src';
import InfoRow from '../../../common/info-row';
import { NewApplicationStatus } from '../../../../../hoc/new-application/types';
import useSourceAndSizeOfWealth from '../../hooks/use-source-and-size-of-wealth';
import { SourceAndSiceOfWealthItemTypes } from '../types';

import cn from '../../styles.module.css';

const SourceAndSiceOfWealthItem = ({
  sourcesOfWealthForIndividual,
  sourceOfWealthForIndividualFiltered,
  sourceOfWealthForIndividual,
  sourceOfWealthForIndividualValues,
  questionnarieSetValue,
  baseCurrency,
  questionnarieControl,
  questionnarieErrors,
  questionnarieClearErrors,
  isEditable,
  status,
}: SourceAndSiceOfWealthItemTypes) => {
  const { setListVisible, listVisible, formValues, onChange, onDelete } =
    useSourceAndSizeOfWealth({
      sourceOfWealthForIndividualValues,
      questionnarieSetValue,
      sourceOfWealthForIndividualValue: sourceOfWealthForIndividual,
    });

  return (
    <div className={cn.sourceOfIncomeItem}>
      <div className={cn.sourceOfIncomeItemTitle}>
        <ClickOutsideDetector setOptionsListVisible={setListVisible}>
          <>
            <button
              type="button"
              className={cn.sourceOfIncomeButton}
              onClick={() => setListVisible(!listVisible)}
              disabled={Boolean(
                status === NewApplicationStatus.ACCEPTED ||
                status === NewApplicationStatus.DECLINED,
              )}
            >
              {formValues?.sourceId
                ? sourcesOfWealthForIndividual?.find(
                    (item) => item.id === formValues?.sourceId,
                  )?.value
                : 'Select option'}
              {status !== NewApplicationStatus.ACCEPTED &&
                status !== NewApplicationStatus.DECLINED && (
                  <>
                    {listVisible ? (
                      <IconUp width={13} height={13} />
                    ) : (
                      <IconDown width={13} height={13} />
                    )}
                  </>
                )}
            </button>
            {listVisible && (
              <div className={cn.sourcesOfIncomeValuesList}>
                {formValues?.sourceId && (
                  <button
                    type="button"
                    className={`${cn.sourcesOfIncomeValuesButton} ${cn.active}`}
                  >
                    {
                      sourcesOfWealthForIndividual?.find(
                        (item) => item.id === formValues?.sourceId,
                      )?.value
                    }
                    <IconSelected />
                  </button>
                )}
                {sourceOfWealthForIndividualFiltered
                  ?.filter((item) => item.id !== formValues?.sourceId)
                  ?.map((item) => (
                    <button
                      type="button"
                      className={cn.sourcesOfIncomeValuesButton}
                      key={item.id}
                      onClick={() =>
                        onChange({
                          target: {
                            name: `sourceId-${sourceOfWealthForIndividual?.id}`,
                            value: item.id,
                          },
                        } as ChangeEvent<HTMLInputElement>)
                      }
                    >
                      {item.value}
                    </button>
                  ))}
              </div>
            )}
          </>
        </ClickOutsideDetector>
      </div>
      <div className={cn.sourceOfIncomeValue}>
        <InputText
          name={`value-${sourceOfWealthForIndividual?.id}`}
          placeholder="Enter annual income..."
          value={formValues?.value}
          size={InputSize.SMALL}
          className="borderless"
          disabled={Boolean(
            status === NewApplicationStatus.ACCEPTED ||
            status === NewApplicationStatus.DECLINED,
          )}
          onChange={(e) =>
            onChange({
              target: {
                name: `value-${sourceOfWealthForIndividual?.id}`,
                value: e.target.value,
              },
            } as ChangeEvent<HTMLInputElement>)
          }
          currency={baseCurrency}
          amountInput
        />
        {status !== NewApplicationStatus.ACCEPTED &&
          status !== NewApplicationStatus.DECLINED &&
          (sourceOfWealthForIndividualValues?.length as number) > 1 && (
            <button
              type="button"
              className={cn.removeSourceOfIncomeButton}
              onClick={onDelete}
            >
              <IconClose width={16} height={16} />
            </button>
          )}
      </div>
      {formValues?.sourceId === '98cdb129-3170-4ff0-8dd9-c0603aafe37c' && (
        <div className={cn.additionalSourceOfIncomeRow}>
          <InfoRow
            title="Specify other source"
            id="sourcesAndSizesOfWealthOther"
            withoutPadding
          >
            <Controller
              control={questionnarieControl}
              name="sourcesAndSizesOfWealthOther"
              defaultValue=""
              render={({ field: { onChange: onChangeValue, value, name } }) => (
                <InputText
                  name={name}
                  placeholder="Describe details..."
                  value={value}
                  size={InputSize.SMALL}
                  onChange={(e) => {
                    onChangeValue(e);
                    questionnarieClearErrors(['sourcesAndSizesOfWealthOther']);
                  }}
                  className="borderless"
                  disabled={
                    !isEditable ||
                    Boolean(
                      status === NewApplicationStatus.ACCEPTED ||
                      status === NewApplicationStatus.DECLINED,
                    )
                  }
                  tabIndex={
                    !isEditable &&
                    Boolean(
                      status === NewApplicationStatus.ACCEPTED ||
                      status === NewApplicationStatus.DECLINED,
                    )
                      ? -1
                      : 0
                  }
                  error={
                    questionnarieErrors?.sourcesAndSizesOfWealthOther?.message
                  }
                />
              )}
            />
          </InfoRow>
        </div>
      )}
      {formValues?.sourceId === 'f1e7067d-3053-460e-a7c4-60ef897f0878' && (
        <div className={cn.additionalSourceOfIncomeRow}>
          <InfoRow
            title="Specify other source"
            id="sourcesAndSizesOfWealthBorrowedOther"
            withoutPadding
          >
            <Controller
              control={questionnarieControl}
              name="sourcesAndSizesOfWealthBorrowedOther"
              defaultValue=""
              render={({ field: { onChange: onChangeValue, value, name } }) => (
                <InputText
                  name={name}
                  placeholder="Describe details..."
                  value={value}
                  size={InputSize.SMALL}
                  onChange={(e) => {
                    onChangeValue(e);
                    questionnarieClearErrors([
                      'sourcesAndSizesOfWealthBorrowedOther',
                    ]);
                  }}
                  className="borderless"
                  disabled={
                    !isEditable ||
                    Boolean(
                      status === NewApplicationStatus.ACCEPTED ||
                      status === NewApplicationStatus.DECLINED,
                    )
                  }
                  tabIndex={
                    !isEditable &&
                    Boolean(
                      status === NewApplicationStatus.ACCEPTED ||
                      status === NewApplicationStatus.DECLINED,
                    )
                      ? -1
                      : 0
                  }
                  error={
                    questionnarieErrors?.sourcesAndSizesOfWealthBorrowedOther
                      ?.message
                  }
                />
              )}
            />
          </InfoRow>
        </div>
      )}
    </div>
  );
};

export default SourceAndSiceOfWealthItem;
